import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlertService, AlertType } from 'src/app/shared/services/alert.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AdministrationTypeEnum, DilutionEnum, FrequencyEnum, PresentationEnum, TreatmentPeriodEnum, UsageEnum } from 'src/app/shared/enum/prescription.enum';
import { MedicRequiredFieldEnum } from 'src/app/shared/enum/medic-required-field.enum';
import { MedicineAdministrationTypeEnum } from 'src/app/shared/enum/medicine-administration-type.enum';
import { MedicineStruct } from 'src/app/shared/services/structs/pharmacy/medicine.struct';
import { AllergyModel } from 'src/app/shared/services/models/medical-record/allergy.model';
import { ConfirmAllergicPatientModalComponent } from 'src/app/shared/components/confirm-allergic-patient-modal/confirm-allergic-patient-modal.component';
import { GetMedicineRequest } from 'src/app/shared/services/requests/pharmacy/get-medicine.request';
import { KitWithItemsStruct } from 'src/app/shared/services/structs/pharmacy/kit-with-items.struct';
import { KitItemsModalComponent } from 'src/app/shared/components/kit-items-modal/kit-items-modal.component';
import { PrescriptionMedicineStruct } from 'src/app/shared/services/structs/pharmacy/prescription-medicine.struct';
import { MedicineLinkedKitService } from 'src/app/shared/services/API/pharmacy/medicine-linked-kit.service';
import { MedicinePresentationTypeEnum } from 'src/app/shared/enum/medicine-presentation-type.enum';
import { TagStruct } from '../../tag/tag.struct';
import { DoseToAdministered } from 'src/app/shared/services/models/pharmacy/dose-to-administered.model';
import { AdministrationLocationEnum } from 'src/app/shared/enum/pharmacy/administration-location.enum';
import { LookupAdministrationLocation } from 'src/app/shared/services/models/pharmacy/lookup-administration-location.model';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { calculateTotalDose24h } from 'src/app/shared/services/prescription-dose-calculator.util';
import { DoseToAdministeredEnum } from 'src/app/shared/enum/pharmacy/dose-to-administered.enum';
import { RelationMedicinePresentationTypeAndDoseToAdministered } from 'src/app/shared/services/models/pharmacy/relation-medicine-presentation-type-and-dose-to-administered.model';

const DOSE_UNIT_IDS = {
  frasco: DoseToAdministeredEnum.Frasco,
  ampola: DoseToAdministeredEnum.Ampola,
  ml: DoseToAdministeredEnum.Ml,
  mg: DoseToAdministeredEnum.Mg,
  g: DoseToAdministeredEnum.G,
  mcg: DoseToAdministeredEnum.Mcg,
  doses: DoseToAdministeredEnum.Doses,
  aerossol: DoseToAdministeredEnum.Aerossol,
  comprimido: DoseToAdministeredEnum.Comprimido,
  capsula: DoseToAdministeredEnum.Capsula
};
import { PharmacyConfig } from 'src/app/shared/services/models/pharmacy/pharmacy-config.model';

@Component({
  selector: 'app-medicine-form',
  templateUrl: './medicine-form.component.html',
  styleUrls: ['./medicine-form.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MedicineFormComponent implements OnInit, OnDestroy {

  constructor(
    public dialog: MatDialog,
    private alertService: AlertService,
    private formBuilder: FormBuilder,
    private medicineLinkedKitService: MedicineLinkedKitService,
  ) { }

  @Input() blockAllergicPrescription: boolean;
  @Input() isFastTrack: boolean;
  @Input() readOnly: boolean;
  @Input() prescriptionMedicineStruct: PrescriptionMedicineStruct;
  @Input() listAllergy: AllergyModel[] = [];
  @Input() selectedMedicineList: MedicineStruct[] = [];
  @Input() medicineList: MedicineStruct[] = [];
  @Input() listSubFrequency: any[];
  @Input() listFrequency: any[];
  @Input() frequencyList: any[] = [];
  @Input() presentationList: any[] = [];
  @Input() treatmentList: any[] = [];
  @Input() dilutionList: any[] = [];
  @Input() measurementUnitList: any[] = [];
  @Input() usageList: any[] = [];
  @Input() dilutionUnitList: any[] = [];
  @Input() administrationTypeList: any[] = [];
  @Input() listDoseToAdministered: DoseToAdministered[] = [];
  @Input() lookupMedicalCareResponse: any;
  @Input() medicConfig: any;
  @Input() listMedicine: any;
  @Input() hasNewStandardPrescription: boolean = false;
  @Input() listLookupAdministrationLocation: LookupAdministrationLocation[] = [];
  @Input() listRelationMedicinePresentationTypeAndDoseToAdministered: RelationMedicinePresentationTypeAndDoseToAdministered[] = [];
  @Input() pharmacyConfig?: PharmacyConfig;

  @Output() save = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<any>();

  public administrationTypeListDisplay: any[] = [];
  public listKit: KitWithItemsStruct[] = [];
  public listTags: TagStruct[];

  public prescriptionModel: FormGroup;
  public selectedMedicine: MedicineStruct;

  public isLoading: boolean;
  public bypassPrescription: boolean = false;
  public undiluted: boolean = false;
  public isFrequency: boolean = false;
  public isLoadingMedicine: boolean = false;

  public idAdministrationType: number;
  public idFrequency: number;
  public idTreatmentPeriod: number;
  public idPresentation: number;
  public idDilution: number;
  public administrationInterna: number = AdministrationLocationEnum.InternaInLoco;
  public administrationExterna: number = AdministrationLocationEnum.ExternaCasa;

  public justification: string;

  public administrationTypeOthersEnum: AdministrationTypeEnum = AdministrationTypeEnum.outros;
  public presentationOthersEnum: PresentationEnum = PresentationEnum.outros;
  public dilutionOthersEnum: DilutionEnum = DilutionEnum.outros;
  public dilutionNoDilutionEnum: DilutionEnum = DilutionEnum.semDiluicao;
  public frequencyEnum: typeof FrequencyEnum = FrequencyEnum;

  public totalDose24hLabel: string | null = null;
  public totalEquivalent24hLabel: string | null = null;

  private destroy$ = new Subject<void>();
  public selectedKitIds: Set<number> = new Set<number>();
  public frequencyNow: any;

  ngOnInit(): void {
    this.initForm();

    if (this.prescriptionMedicineStruct?.prescriptionMedicine) {
      this.populateValues();
      const idMedicine = this.prescriptionMedicineStruct.prescriptionMedicine.idMedicine;
      if (idMedicine) {
        this.verifyKits(idMedicine);
      }
    }

    this.verifyRequiredFields();
    this.subscribeToTotalDoseCalculation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.prescriptionModel = this.formBuilder.group({
      idMedicine: [{ value: null, disabled: this.readOnly }, Validators.required],
      quantity: [{ value: null, disabled: this.readOnly }, Validators.max(32767)],
      idAdministrationType: [{ value: null, disabled: this.readOnly }],
      idFrequency: [{ value: '', disabled: this.readOnly }, Validators.required],
      idPresentation: [{ value: null, disabled: this.readOnly }],
      idDilution: [{ value: null, disabled: this.readOnly }],
      idTreatmentPeriod: [{ value: '', disabled: this.readOnly }, Validators.required],
      presentation: [{ value: '', disabled: this.readOnly }],
      dilution: [{ value: '', disabled: this.readOnly }],
      administrationType: [{ value: '', disabled: this.readOnly }],
      observation: [{ value: '', disabled: this.readOnly }],
      idMeasurementUnit: [{ value: null, disabled: this.readOnly }],
      idSubFrequency: [{ value: '', disabled: this.readOnly }],
      idDilutionUnit: [{ value: null, disabled: this.readOnly }],
      dilutionQuantity: [{ value: '', disabled: this.readOnly }],
      idDoseToAdministered: [{ value: null, disabled: this.readOnly }],
      idLookupAdministrationLocation: [{ value: AdministrationLocationEnum.InternaInLoco, disabled: this.readOnly }, Validators.required],    });
  }

  private subscribeToTotalDoseCalculation(): void {
    const controlsToWatch = ['quantity', 'idFrequency', 'idDoseToAdministered', 'idMedicine'];

    controlsToWatch.forEach(controlName => {
      this.prescriptionModel.get(controlName)?.valueChanges.pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      ).subscribe(() => this.recalculateTotalDose());
    });

    this.recalculateTotalDose();
  }

  private recalculateTotalDose(): void {
    this.totalDose24hLabel = null;
    this.totalEquivalent24hLabel = null;

    const quantity = this.prescriptionModel.get('quantity')?.value;
    const idFrequency = this.prescriptionModel.get('idFrequency')?.value;
    const idDoseToAdministered: number = this.prescriptionModel.get('idDoseToAdministered')?.value;
    const idMedicine = this.prescriptionModel.get('idMedicine')?.value;
    const idMeasurementUnit = this.prescriptionModel.get('idMeasurementUnit')?.value;

    const result = calculateTotalDose24h(quantity, idFrequency, this.frequencyList);
    if (!result) return;

    this.totalDose24hLabel = result.label;

    const medicine = this.medicineList.find(f => f.medicine.idMedicine === idMedicine)
      ?? this.selectedMedicine;
    if (!medicine) return;

    const med = medicine.medicine;
    const countMeasurementUnit = med.countMeasurementUnit;
    if (!countMeasurementUnit || countMeasurementUnit <= 0) return;

    const measurementUnit = this.measurementUnitList?.find(m => m.idMeasurementUnit === idMeasurementUnit);
    const unitNameDisplay = measurementUnit?.measurementUnitName ?? 'Unidade(s)';

    const totalUnits = result.total / countMeasurementUnit;

    if (idDoseToAdministered === DOSE_UNIT_IDS.ml) {
      if (measurementUnit?.idMeasurementUnit === 4) { // idMeasurementUnit=4 é ml
        this.totalEquivalent24hLabel = `(Equivale a ${this.formatNumber(totalUnits)} ${unitNameDisplay})`;
      }
      return;
    }

    const mgId = DoseToAdministeredEnum.Mg ?? DOSE_UNIT_IDS.mg;
    const gId = DoseToAdministeredEnum.G ?? DOSE_UNIT_IDS.g;
    const mcgId = DoseToAdministeredEnum.Mcg ?? DOSE_UNIT_IDS.mcg;

    if (idDoseToAdministered === mgId || idDoseToAdministered === gId || idDoseToAdministered === mcgId) {
      const unitToMgFactor = this.getUnitToMgFactor(idDoseToAdministered);
      const total24hMg = result.total * unitToMgFactor;
      const units24h = total24hMg / countMeasurementUnit;
      this.totalEquivalent24hLabel = `(Equivale a ${this.formatNumber(units24h)} ${unitNameDisplay})`;
      return;
    }

    if (idDoseToAdministered === DOSE_UNIT_IDS.doses ||
      idDoseToAdministered === DOSE_UNIT_IDS.aerossol ||
      idDoseToAdministered === DOSE_UNIT_IDS.comprimido ||
      idDoseToAdministered === DOSE_UNIT_IDS.capsula) {
      this.totalEquivalent24hLabel = `(Equivale a ${this.formatNumber(totalUnits)} ${unitNameDisplay})`;
      return;
    }
  }

  private getUnitToMgFactor(idDoseToAdministered: number): number {
    const gId = DoseToAdministeredEnum.G ?? DOSE_UNIT_IDS.g;
    const mcgId = DoseToAdministeredEnum.Mcg ?? DOSE_UNIT_IDS.mcg;
    const mgId = DoseToAdministeredEnum.Mg ?? DOSE_UNIT_IDS.mg;

    if (idDoseToAdministered === gId) return 1000;
    if (idDoseToAdministered === mcgId) return 0.001;
    return 1;
  }

  private formatNumber(value: number): string {
    if (value === null || value === undefined || isNaN(value)) return '—';
    return parseFloat(value.toFixed(2)).toLocaleString('pt-BR');
  }

  populateValues() {
    this.selectedMedicine = this.medicineList.find(
      f => f.medicine.idMedicine == this.prescriptionMedicineStruct.prescriptionMedicine.idMedicine
    );
    let naoDefinida = this.administrationTypeList?.find(
      f => f.idMedicineAdministrationType == MedicineAdministrationTypeEnum.NaoDefinida
    );
    this.administrationTypeListDisplay = this.administrationTypeList?.filter(
      f => this.selectedMedicine?.listIdMedicineAdministrationType?.includes(f.idMedicineAdministrationType)
    );

    if (naoDefinida && !this.administrationTypeListDisplay?.some(f => f.idMedicineAdministrationType === naoDefinida.idMedicineAdministrationType)) {
      this.administrationTypeListDisplay?.unshift(naoDefinida);
    }

    this.prescriptionModel.get('idMedicine')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.idMedicine ?? null
    );
    this.prescriptionModel.get('quantity')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.quantity?.toString() ?? ''
    );
    this.prescriptionModel.get('idAdministrationType')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.idAdministrationType ?? null
    );
    this.prescriptionModel.get('idFrequency')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.idFrequency ?? null
    );
    this.prescriptionModel.get('idDoseToAdministered')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.idDoseToAdministered ?? null
    );
    this.prescriptionModel.get('idLookupAdministrationLocation')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.idLookupAdministrationLocation ?? AdministrationLocationEnum.InternaInLoco
    );
    this.onChangeFrequency(this.prescriptionMedicineStruct.prescriptionMedicine.idFrequency);
    this.prescriptionModel.get('idPresentation')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.idPresentation ?? null
    );
    this.prescriptionModel.get('idTreatmentPeriod')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.idTreatmentPeriod ?? null
    );
    this.prescriptionModel.get('observation')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.observation
    );
    this.prescriptionModel.get('idDilution')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.idDilution ?? null
    );
    this.prescriptionModel.get('dilution')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.dilution
    );
    this.prescriptionModel.get('presentation')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.presentation
    );
    this.prescriptionModel.get('administrationType')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.administrationType
    );
    this.prescriptionModel.get('idMeasurementUnit')?.setValue(
      this.prescriptionMedicineStruct.prescriptionMedicine.idMeasurementUnit
    );

    this.prescriptionMedicineStruct.listSelectedIdKit.forEach(
      idKit => this.selectedKitIds.add(idKit)
    );

    if (this.prescriptionMedicineStruct.prescriptionMedicine.idSubFrequency != null) {
      this.isFrequency = true;
      this.prescriptionModel.get('idSubFrequency')?.setValue(
        this.prescriptionMedicineStruct.prescriptionMedicine.idSubFrequency
      );
    }

    if (this.prescriptionMedicineStruct.prescriptionMedicine.idDilutionUnit != null) {
      this.undiluted = true;
      this.prescriptionModel.get('idDilutionUnit')?.setValue(
        this.prescriptionMedicineStruct.prescriptionMedicine.idDilutionUnit
      );
      this.prescriptionModel.get('dilutionQuantity')?.setValue(
        this.prescriptionMedicineStruct.prescriptionMedicine.dilutionQuantity
      );
    }

    this.justification = this.prescriptionMedicineStruct.prescriptionMedicine.justification ?? '';

    if (this.readOnly) {
      this.idAdministrationType = this.prescriptionMedicineStruct.prescriptionMedicine.idAdministrationType;
      this.idFrequency = this.prescriptionMedicineStruct.prescriptionMedicine.idFrequency;
      this.idTreatmentPeriod = this.prescriptionMedicineStruct.prescriptionMedicine.idTreatmentPeriod;
    }
  }

  clickCancel() {
    this.cancel.emit();
  }

  onChangeMedicine(event: any) {
    this.selectedMedicine = this.medicineList.find(f => f.medicine.idMedicine == event.value);
    if (!this.selectedMedicine) return;

    const listId: number[] = this.selectedMedicine.listIdMedicineAdministrationType || [];
    const naoDefinida = this.administrationTypeList.find(
      f => f.idMedicineAdministrationType == MedicineAdministrationTypeEnum.NaoDefinida
    );
    this.administrationTypeListDisplay = this.administrationTypeList.filter(
      f => listId.includes(f.idMedicineAdministrationType)
    );

    const ctrlAdminType = this.prescriptionModel.get('idAdministrationType');

    if (this.administrationTypeListDisplay.length > 0) {
      if (naoDefinida && !this.administrationTypeListDisplay.some(f => f.idMedicineAdministrationType === naoDefinida.idMedicineAdministrationType)) {
        this.administrationTypeListDisplay.unshift(naoDefinida);
      }
      
      const valueToSet = this.administrationTypeListDisplay.length === 2
        ? this.administrationTypeListDisplay[1].idMedicineAdministrationType
        : '';
      ctrlAdminType?.setValue(valueToSet);
      ctrlAdminType?.enable();
    } else {
      this.administrationTypeListDisplay = [...this.administrationTypeList];
      ctrlAdminType?.setValue(naoDefinida?.idAdministrationType);
      ctrlAdminType?.disable();
      this.alertService.show('Aviso', 'Não há nenhuma via cadastrada para este medicamento', AlertType.warning);
    }

    const { idDilution, idMeasurementUnit, idMedicinePresentationType, medicinePresentationOther } =
      this.selectedMedicine.medicine;

    this.prescriptionModel.get('idDilution')?.setValue(idDilution || null);

    const ctrlMeasUnit = this.prescriptionModel.get('idMeasurementUnit');
    if (idMeasurementUnit) {
      ctrlMeasUnit?.setValue(idMeasurementUnit);
      ctrlMeasUnit?.disable();
    } else {
      ctrlMeasUnit?.setValue(null);
      ctrlMeasUnit?.enable();
    }

    const ctrlPres = this.prescriptionModel.get('idPresentation');
    const ctrlPresText = this.prescriptionModel.get('presentation');

    if (idMedicinePresentationType) {
      ctrlPres?.setValue(idMedicinePresentationType);
      ctrlPres?.disable();
      if (idMedicinePresentationType == MedicinePresentationTypeEnum.Outro) {
        ctrlPresText?.setValue(medicinePresentationOther);
      }
    } else {
      ctrlPres?.setValue(null);
      ctrlPres?.enable();
      ctrlPresText?.setValue(null);
    }

    if (this.selectedMedicine.listAllergy?.length > 0) {
      const allergyDetected = this.selectedMedicine.listAllergy.some(
        it => this.listAllergy.find(it2 => it2.idAllergy == it)
      );

      if (allergyDetected) {
        const dialogRef = this.dialog.open(ConfirmAllergicPatientModalComponent, {
          data: { blockAllergicPrescription: this.blockAllergicPrescription },
          disableClose: true
        });

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe({
          next: (result) => {
            if (result?.cancel) {
              this.prescriptionModel.patchValue({
                idMedicine: '',
                idAdministrationType: '',
                idDilution: '',
                idMeasurementUnit: null,
                idPresentation: null,
                idDoseToAdministered: null,
              });
              ctrlAdminType?.enable();
            } else if (result && !result.cancel && result.justification) {
              this.justification = result.justification;
              this.verifyKits(event.value);
            }
          }
        });
        return;
      }
    }

    this.verifyKits(event.value);
  }

  verifyKits(idMedicine: number) {
    this.isLoadingMedicine = true;
    let getMedicineRequest = new GetMedicineRequest();
    getMedicineRequest.listIdMedicine.push(idMedicine);
    this.searchKits(getMedicineRequest);
  }

  onChangePresentation(event: number) {
    this.idPresentation = event;
    if (this.idPresentation == this.presentationOthersEnum.valueOf()) {
      this.prescriptionModel.get('presentation')?.setValidators(Validators.required);
    } else {
      this.prescriptionModel.get('presentation')?.setValue(null);
      this.prescriptionModel.get('presentation')?.setValidators(Validators.nullValidator);
    }
    this.prescriptionModel.get('presentation')?.updateValueAndValidity();
  }

  onChangeAdministrationType(event: number) {
    this.idAdministrationType = event;
    if (this.idAdministrationType == this.administrationTypeOthersEnum.valueOf()) {
      this.prescriptionModel.get('administrationType')?.setValue(
        this.selectedMedicine.medicine.medicineAdministrationOther
      );
      this.prescriptionModel.get('administrationType')?.setValidators(Validators.required);
    } else {
      this.prescriptionModel.get('administrationType')?.setValue(null);
      this.prescriptionModel.get('administrationType')?.setValidators(Validators.nullValidator);
    }
    this.prescriptionModel.get('administrationType')?.updateValueAndValidity();
  }

  onChangeDilution(event: number) {
    this.idDilution = event;
    this.undiluted = this.idDilution != this.dilutionNoDilutionEnum.valueOf() && this.idDilution != null;

    if (this.idDilution == this.dilutionOthersEnum.valueOf()) {
      this.prescriptionModel.get('dilution')?.setValidators(Validators.required);
    } else {
      this.prescriptionModel.get('dilution')?.setValue(null);
      this.prescriptionModel.get('dilution')?.setValidators(Validators.nullValidator);
    }
    this.prescriptionModel.get('dilution')?.updateValueAndValidity();
  }

  onKey(value: string) {
    this.selectedMedicineList = this.search(value);
  }

  search(value: string): MedicineStruct[] {
    const filter = value.toLowerCase();
    return this.medicineList.filter(
      option => option.medicine.medicineDescription.toLowerCase().includes(filter)
    );
  }

  private parseQuantitySafe(val: any): number | null {
    if (val === null || val === undefined || val === '') return null;
    if (typeof val === 'number') return val;
    const sanitized = String(val).replace(',', '.');
    const parsed = parseFloat(sanitized);
    return isNaN(parsed) ? null : parsed;
  }

  onSubmit() {
    this.prescriptionModel.markAllAsTouched();

    if (this.prescriptionModel.invalid) {
      this.alertService.show('Erro', 'Preencha todos os campos obrigatórios!', AlertType.error);
      return;
    }

    if (this.hasNewStandardPrescription &&
      this.prescriptionModel.get('idFrequency') &&
      this.prescriptionModel.get('idTreatmentPeriod') &&
      this.prescriptionModel.get('idFrequency')?.value == this.frequencyEnum.now &&
      this.prescriptionModel.get('idTreatmentPeriod')?.value != TreatmentPeriodEnum.UmDia
    ) {
      this.alertService.show(
        'Erro',
        'Para essa frequência, o período de tratamento tem que ser apenas de um dia.',
        AlertType.error
      );
      return;
    }

    this.prescriptionMedicineStruct = new PrescriptionMedicineStruct();
    this.prescriptionMedicineStruct.prescriptionMedicine.idMedicine =
      this.prescriptionModel.get('idMedicine')?.value;
    this.prescriptionMedicineStruct.medicineName =
      this.medicineList.find(
        c => c.medicine.idMedicine == this.prescriptionMedicineStruct.prescriptionMedicine.idMedicine
      ).medicine.medicineDescription;

    const location = this.listLookupAdministrationLocation.find(
      c => c.idLookupAdministrationLocation === this.prescriptionModel.get('idLookupAdministrationLocation')?.value
    );
    this.prescriptionMedicineStruct.lookupAdministrationLocationName = location?.name ?? null;
    this.prescriptionMedicineStruct.controlledMedicine = this.selectedMedicine.medicine.controlledMedicine;
    this.prescriptionMedicineStruct.prescriptionMedicine.idUsage = UsageEnum.interno;
    this.prescriptionMedicineStruct.usage =
      this.usageList.find(
        c => c.idUsage == this.prescriptionMedicineStruct.prescriptionMedicine.idUsage
      ).usageName;

    this.prescriptionMedicineStruct.prescriptionMedicine.idAdministrationType =
      this.prescriptionModel.get('idAdministrationType')?.value
        ? this.prescriptionModel.get('idAdministrationType')?.value
        : this.administrationTypeList.find(
          x => x.idMedicineAdministrationType == MedicineAdministrationTypeEnum.NaoDefinida
        ).idMedicineAdministrationType;

    this.prescriptionMedicineStruct.prescriptionMedicine.administrationType =
      this.prescriptionModel.get('administrationType')?.value;
    this.prescriptionMedicineStruct.administrationTypeName =
      this.prescriptionMedicineStruct.prescriptionMedicine.idAdministrationType
        ? this.administrationTypeList.find(
          x => x.idMedicineAdministrationType == this.prescriptionMedicineStruct.prescriptionMedicine.idAdministrationType
        ).medicineAdministrationTypeName
        : null;

    this.prescriptionMedicineStruct.prescriptionMedicine.quantity =
      this.parseQuantitySafe(this.prescriptionModel.get('quantity')?.value);
    this.prescriptionMedicineStruct.prescriptionMedicine.idPresentation =
      this.prescriptionModel.get('idPresentation')?.value ?? null;
    this.prescriptionMedicineStruct.presentationName =
      this.prescriptionMedicineStruct.prescriptionMedicine.idPresentation
        ? this.presentationList.find(
          c => c.idMedicinePresentationType == this.prescriptionMedicineStruct.prescriptionMedicine.idPresentation
        ).medicinePresentationTypeName
        : null;
    this.prescriptionMedicineStruct.prescriptionMedicine.presentation =
      this.prescriptionModel.get('presentation')?.value;
    this.prescriptionMedicineStruct.prescriptionMedicine.idFrequency =
      this.prescriptionModel.get('idFrequency')?.value ?? null;
    this.prescriptionMedicineStruct.frequencyName =
      this.prescriptionMedicineStruct.prescriptionMedicine.idFrequency
        ? this.frequencyList.find(
          c => c.idFrequency == this.prescriptionMedicineStruct.prescriptionMedicine.idFrequency
        ).frequencyName
        : null;
    this.prescriptionMedicineStruct.prescriptionMedicine.idTreatmentPeriod =
      this.prescriptionModel.get('idTreatmentPeriod')?.value ?? null;
    this.prescriptionMedicineStruct.treatmentPeriodName =
      this.prescriptionMedicineStruct.prescriptionMedicine.idTreatmentPeriod
        ? this.treatmentList.find(
          c => c.idTreatmentPeriod == this.prescriptionMedicineStruct.prescriptionMedicine.idTreatmentPeriod
        ).treatmentPeriodName
        : null;
    this.prescriptionMedicineStruct.prescriptionMedicine.idDilution =
      this.prescriptionModel.get('idDilution')?.value ?? null;
    this.prescriptionMedicineStruct.dilutionName =
      this.dilutionList.find(
        c => c.idDilution == this.prescriptionMedicineStruct.prescriptionMedicine.idDilution
      )?.dilutionName ?? null;
    this.prescriptionMedicineStruct.prescriptionMedicine.dilution =
      this.prescriptionModel.get('dilution')?.value;
    this.prescriptionMedicineStruct.prescriptionMedicine.idMeasurementUnit =
      this.prescriptionModel.get('idMeasurementUnit')?.value;
    this.prescriptionMedicineStruct.prescriptionMedicine.observation =
      this.prescriptionModel.get('observation')?.value;
    this.prescriptionMedicineStruct.prescriptionMedicine.idSubFrequency =
      this.prescriptionModel.get('idSubFrequency')?.value ?? null;
    this.prescriptionMedicineStruct.prescriptionMedicine.idDilutionUnit =
      this.prescriptionModel.get('idDilutionUnit')?.value ?? null;
    this.prescriptionMedicineStruct.prescriptionMedicine.dilutionQuantity =
      this.prescriptionModel.get('dilutionQuantity')?.value;
    this.prescriptionMedicineStruct.prescriptionMedicine.idDoseToAdministered =
      this.prescriptionModel.get('idDoseToAdministered')?.value;
    this.prescriptionMedicineStruct.prescriptionMedicine.idLookupAdministrationLocation =
      this.prescriptionModel.get('idLookupAdministrationLocation')?.value;
    this.prescriptionMedicineStruct.listSelectedIdKit = Array.from(this.selectedKitIds.values());
    this.prescriptionMedicineStruct.listTags = this.selectedMedicine.listTags;

    if (this.prescriptionMedicineStruct.prescriptionMedicine.idSubFrequency != null) {
      this.prescriptionMedicineStruct.subFrequencyName =
        this.listSubFrequency.find(
          x => x.idSubFrequency == this.prescriptionMedicineStruct.prescriptionMedicine.idSubFrequency
        ).subFrequencyName;
    }

    if (this.prescriptionMedicineStruct.prescriptionMedicine.idDilutionUnit != null) {
      this.prescriptionMedicineStruct.dilutionUnitName =
        this.dilutionUnitList.find(
          x => x.idDilutionUnit == this.prescriptionMedicineStruct.prescriptionMedicine.idDilutionUnit
        )?.dilutionUnitName;
    }

    if (this.justification) {
      this.prescriptionMedicineStruct.prescriptionMedicine.justification = this.justification;
    }

    const freqResult = calculateTotalDose24h(
      this.prescriptionModel.get('quantity')?.value,
      this.prescriptionModel.get('idFrequency')?.value,
      this.frequencyList
    );
    this.prescriptionMedicineStruct.prescriptionMedicine.calculatedTotalQuantity =
      freqResult ? freqResult.total : null;

    this.save.emit({
      prescriptionMedicine: this.prescriptionMedicineStruct,
      bypassPrescription: this.bypassPrescription
    });
  }

  verifyRequiredFields() {
    if (!this.medicConfig?.listIdRequiredField) return;

    const fieldMap: Record<number, string> = {
      [MedicRequiredFieldEnum.prescriptionQuantity]: 'quantity',
      [MedicRequiredFieldEnum.prescriptionAdministrationType]: 'idAdministrationType',
      [MedicRequiredFieldEnum.prescriptionDilution]: 'idDilution',
      [MedicRequiredFieldEnum.prescriptionPresentation]: 'idPresentation',
      [MedicRequiredFieldEnum.prescriptionObservation]: 'observation',
      [MedicRequiredFieldEnum.prescriptionMeasurementUnit]: 'idMeasurementUnit'
    };

    this.medicConfig.listIdRequiredField.forEach((x: number) => {
      const controlName = fieldMap[x];
      if (controlName) {
        const ctrl = this.prescriptionModel.get(controlName);
        const validators = [Validators.required];
        if (x == MedicRequiredFieldEnum.prescriptionQuantity) {
          validators.push(Validators.max(32767));
        }
        ctrl?.setValidators(validators);
        ctrl?.updateValueAndValidity();
      }
    });
  }

  toggleBypassPrescription(event: any) {
    if (event.checked) {
      this.bypassPrescription = true;
      this.selectedMedicineList = this.listMedicine.listMedicineStruct;
    } else {
      this.bypassPrescription = false;
      this.selectedMedicineList = this.listMedicine.listMedicineStruct.filter(
        (x: any) => x.medicine.fastTrack == true
      );
      const idMedicine: number = this.prescriptionModel.get('idMedicine')?.value;
      const medicine: MedicineStruct = this.listMedicine.listMedicineStruct.find(
        (x: any) => x.medicine.idMedicine == idMedicine
      );
      if (!this.selectedMedicineList.includes(medicine)) {
        this.prescriptionModel.get('idMedicine')?.setValue(null);
      }
    }
  }

  onChangeFrequency(event: number) {
    if (
      event == this.frequencyEnum.ifNecessary ||
      event == this.frequencyEnum.medicCriteria ||
      event == this.frequencyEnum.continuous
    ) {
      this.isFrequency = true;
      this.prescriptionModel.get('idSubFrequency')?.setValidators([Validators.required]);
      this.prescriptionModel.get('idSubFrequency')?.updateValueAndValidity();
    } else {
      this.isFrequency = false;
      this.prescriptionModel.get('idSubFrequency')?.setValue(null);
      this.prescriptionModel.get('idSubFrequency')?.setValidators([]);
      this.prescriptionModel.get('idSubFrequency')?.updateValueAndValidity();
    }

    if (this.hasNewStandardPrescription) {
      if (event == this.frequencyEnum.now) {
        this.prescriptionModel.get('idTreatmentPeriod')?.setValue(TreatmentPeriodEnum.UmDia);
        this.prescriptionModel.get('idTreatmentPeriod')?.disable();
      } else {
        this.prescriptionModel.get('idTreatmentPeriod')?.enable();
      }
    }
    this.prescriptionModel.get('idTreatmentPeriod')?.updateValueAndValidity();
  }

  searchKits(getMedicineRequest: GetMedicineRequest) {
    this.medicineLinkedKitService.getWithItemsByIdMedicine(getMedicineRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoadingMedicine = false;
          if (response.isError) return;
          this.listKit = response?.listKit?.length > 0 ? response.listKit : [];
        },
        error: (error) => {
          console.error(error);
          this.isLoadingMedicine = false;
        }
      });
  }

  openKit(kit: KitWithItemsStruct) {
    this.dialog.open(KitItemsModalComponent, {
      width: '700px',
      data: { kit }
    });
  }

  isKitSelected(item: KitWithItemsStruct): boolean {
    return this.selectedKitIds.has(item.kit.idKit);
  }

  toggleKitSelection(item: KitWithItemsStruct): void {
    const id = item.kit.idKit;
    if (this.selectedKitIds.has(id)) {
      this.selectedKitIds.delete(id);
    } else {
      this.selectedKitIds.clear();
      this.selectedKitIds.add(id);
    }
  }

  get doseToAdministered(): string {
    if (!this.prescriptionModel.get('idDoseToAdministered')?.value) return '';
    const unit = this.listDoseToAdministered?.find(
      u => u.idDoseToAdministered === this.prescriptionModel.get('idDoseToAdministered')?.value
    );
    return unit?.name ?? '';
  }

  isDoseToAdministeredDisabled(idDoseToAdministered: number): boolean {
    const selectedPresentation = this.prescriptionModel.get('idPresentation')?.value;
    if (!selectedPresentation) {
      return false;
    }
    const relationsForPresentation = this.listRelationMedicinePresentationTypeAndDoseToAdministered.filter(
      r => r.idMedicinePresentationType === selectedPresentation
    );
    if (relationsForPresentation.length === 0) {
      return false;
    }
    return !relationsForPresentation.some(r => r.idDoseToAdministered === idDoseToAdministered);
  }
}